import moment from 'moment';
import { useMemo } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';

import { useLocalize, useViewer } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Avatar, Container, FlexGap, Heading3, Paragraph, Title, tokens } from '@treams/treams-ui';

import { TalentReviewSnapshotDateNavigation } from './components/TalentReviewDateNavigation';
import TalentReviewGrid from './components/TalentReviewGrid';
import { talentReviewPositionCopy } from './talentReviewShared';

/**
 * The page a placed person opens from the shared-review email: their cell,
 * their motivation, who shared it, and the grid with only their cell marked.
 * @param props component props
 * @param props.snapshot the shared snapshot, placements already filtered for this viewer
 */
export default function TalentReviewSharedPage(props: {
	snapshot: graphql.TalentReviewSnapshotQuery['talentReviewSnapshot'];
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();
	const viewer = useViewer();
	const { snapshot } = props;

	const snapshotsQuery = graphql.useTalentReviewSharedSnapshotsQuery({
		variables: { id: snapshot.group.id },
	});

	const stops = useMemo(() => {
		const nodes =
			snapshotsQuery.data?.group?.talentReviewSnapshots.edges.map(({ node }) => node) ?? [];
		const shared = nodes.filter(
			(node) =>
				node.viewerIsPlaced &&
				node.template.id === snapshot.template.id &&
				node.visibility !== graphql.TalentReviewVisibility.Manager,
		);
		const included = shared.some((node) => node.id === snapshot.id)
			? shared
			: [...shared, snapshot];

		return [...included]
			.sort((a, b) => {
				if (a.localDate !== b.localDate) {
					return a.localDate < b.localDate ? -1 : 1;
				}

				return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
			})
			.map((node) => ({ id: node.id, localDate: node.localDate }));
	}, [snapshot, snapshotsQuery.data]);

	const placement = snapshot.placements.find((item) => item.user.id === viewer?.id);
	const cell = placement ? snapshot.template.cells[placement.row]?.[placement.column] : undefined;
	const text = (value?: graphql.LocalizedString) => (value ? localize(value)?.text : undefined);
	const position = talentReviewPositionCopy({
		cellLabel: text(cell?.label),
		performanceLabel: text(snapshot.template.yAxis.label),
		performanceTick: placement ? text(snapshot.template.yAxis.ticks[placement.row]) : undefined,
		potentialLabel: text(snapshot.template.xAxis.label),
		potentialTick: placement ? text(snapshot.template.xAxis.ticks[placement.column]) : undefined,
	});
	const motivation = placement?.motivation?.trim();
	const sharer = snapshot.sharedBy ?? snapshot.createdBy;
	const sharedAt = snapshot.sharedAt ?? snapshot.createdAt;
	const eyebrow = t('talentReviews.v2.shared.eyebrow');

	return (
		<Container>
			<Helmet>
				<title>{position.title ?? eyebrow}</title>
			</Helmet>
			<FlexGap flexDirection="column" gap={tokens.gap.section} py={4}>
				<Flex alignItems="center" justifyContent="space-between">
					<Heading3 m={0}>{eyebrow}</Heading3>
					<TalentReviewSnapshotDateNavigation
						localDate={snapshot.localDate}
						snapshotID={snapshot.id}
						snapshots={stops}
					/>
				</Flex>
				<FlexGap flexDirection="column" gap={tokens.gap.group}>
					{position.title && (
						<Title as="h1" style={{ margin: 0 }}>
							{position.title}
						</Title>
					)}
					{position.subtitle && <Paragraph m={0}>{position.subtitle}</Paragraph>}
					{motivation && (
						<Paragraph m={0} style={{ color: tokens.text.primary }}>
							“{motivation}”
						</Paragraph>
					)}
					<FlexGap alignItems="center" gap={tokens.gap.inline}>
						<Avatar size={24} user={sharer} />
						<Paragraph small m={0}>
							{t('talentReviews.v2.shared.sharedBy', {
								name: `${sharer.firstName} ${sharer.lastName}`,
								date: moment(sharedAt).format('LL'),
							})}
						</Paragraph>
					</FlexGap>
				</FlexGap>
				<DragDropContext onDragEnd={() => undefined}>
					<TalentReviewGrid
						template={snapshot.template}
						placements={snapshot.placements as graphql.TalentReviewGridPlacement[]}
						readonly
						emphasizeUserID={viewer?.id}
					/>
				</DragDropContext>
			</FlexGap>
		</Container>
	);
}
